import React, { useEffect, useState } from 'react';
import {
  getPromotionCategoriesAdmin,
  createCategory,
  updatePromotionCategory,
  PromotionCategory,
} from '../services/api';
import './AdminCategoryModules.css';

const emptyForm = { name: '', description: '', display_order: 0, is_active: 1 as 0 | 1 };

const AdminCategoryModules: React.FC = () => {
  const [rows, setRows] = useState<PromotionCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState(emptyForm);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await getPromotionCategoriesAdmin();
      setRows(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRow.name.trim()) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await createCategory({
        name: newRow.name.trim(),
        description: newRow.description.trim() || undefined,
        display_order: Number(newRow.display_order) || 0,
      });
      setNewRow(emptyForm);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveRow = async (cat: PromotionCategory, draft: Partial<PromotionCategory>) => {
    setSavingId(cat.id);
    setError('');
    try {
      await updatePromotionCategory(cat.id, {
        name: draft.name != null ? String(draft.name).trim() : cat.name,
        description: draft.description ?? cat.description,
        display_order: draft.display_order != null ? Number(draft.display_order) : cat.display_order,
        is_active: draft.is_active != null ? Number(draft.is_active) : cat.is_active,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="admin-category-modules loading">Loading category modules…</div>;
  }

  return (
    <div className="admin-category-modules">
      <p className="admin-category-modules-intro">
        Home screen <strong>category modules</strong>: title, subtext (description), global sort order, and active
        state. Order per society is set under{' '}
        <strong>Societies → society detail → Category order</strong>.
      </p>

      {error && <div className="admin-category-modules-error">{error}</div>}

      <section className="admin-category-modules-card">
        <h2>Add category</h2>
        <form className="admin-category-modules-form" onSubmit={handleCreate}>
          <label>
            <span>Name</span>
            <input
              value={newRow.name}
              onChange={(e) => setNewRow((s) => ({ ...s, name: e.target.value }))}
              placeholder="e.g. Discounts & Offers"
            />
          </label>
          <label>
            <span>Subtext</span>
            <input
              value={newRow.description}
              onChange={(e) => setNewRow((s) => ({ ...s, description: e.target.value }))}
              placeholder="Short line under the title"
            />
          </label>
          <label>
            <span>Global order</span>
            <input
              type="number"
              value={newRow.display_order}
              onChange={(e) => setNewRow((s) => ({ ...s, display_order: parseInt(e.target.value, 10) || 0 }))}
            />
          </label>
          <button type="submit" disabled={creating}>
            {creating ? 'Adding…' : 'Add category'}
          </button>
        </form>
      </section>

      <section className="admin-category-modules-card">
        <h2>All categories</h2>
        <div className="admin-category-modules-table-wrap">
          <table className="admin-category-modules-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Subtext</th>
                <th>Global order</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty">
                    No categories yet.
                  </td>
                </tr>
              ) : (
                rows.map((cat) => (
                  <CategoryEditRow
                    key={cat.id}
                    cat={cat}
                    saving={savingId === cat.id}
                    onSave={handleSaveRow}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

const CategoryEditRow: React.FC<{
  cat: PromotionCategory;
  saving: boolean;
  onSave: (cat: PromotionCategory, draft: Partial<PromotionCategory>) => void;
}> = ({ cat, saving, onSave }) => {
  const [name, setName] = useState(cat.name);
  const [description, setDescription] = useState(cat.description ?? '');
  const [display_order, setDisplayOrder] = useState(cat.display_order);
  const [is_active, setIsActive] = useState(cat.is_active);

  useEffect(() => {
    setName(cat.name);
    setDescription(cat.description ?? '');
    setDisplayOrder(cat.display_order);
    setIsActive(cat.is_active);
  }, [cat]);

  return (
    <tr>
      <td>
        <input
          className="cell-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </td>
      <td>
        <input
          className="cell-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Subtext"
        />
      </td>
      <td>
        <input
          type="number"
          className="cell-input narrow"
          value={display_order}
          onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
        />
      </td>
      <td>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={is_active === 1}
            onChange={(e) => setIsActive(e.target.checked ? 1 : 0)}
          />
        </label>
      </td>
      <td>
        <button
          type="button"
          className="btn-save-row"
          disabled={saving}
          onClick={() =>
            onSave(cat, {
              name,
              description: description || null,
              display_order,
              is_active,
            })
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

export default AdminCategoryModules;
